import { useAppContext } from 'context/useAppContext';
import { useFormikContext } from 'formik';
import { IIdentityFormValues } from '../pages/KycForm';
import { formatAmountWithCommas } from 'utils/helpers';
import { useEffect, useMemo } from 'react';

type TenureRate = '2.5' | '7.5' | '15';

interface PaymentScheduleItem {
  paymentType: string;
  amount: number;
  date: Date;
}

const BOOKING_FEE = 25000;

const tableHeader = ['Installment', 'Amount', 'Payday'];

function generatePaymentSchedule(
  initialPayment: number,
  balanceAmount: number,
  rate: TenureRate | number
): PaymentScheduleItem[] {
  const rateMap: Record<TenureRate, number> = {
    '2.5': 1,
    '7.5': 3,
    '15': 6,
  };

  const rateKey = rate.toString() as TenureRate;
  const numInstallments = rateMap[rateKey];

  if (!numInstallments) {
    throw new Error('Invalid rate. Must be 2.5, 7.5, or 15');
  }

  const schedule = [];
  const today = new Date();

  schedule.push({
    paymentType: 'Initial Payment',
    amount: initialPayment,
    date: today,
  });

  const finalPaymentDate = new Date(today);
  finalPaymentDate.setMonth(finalPaymentDate.getMonth() + numInstallments);

  const newSchedule = schedule.concat([
    {
      paymentType: 'Final Payment',
      amount: balanceAmount,
      date: finalPaymentDate,
    },
    {
      paymentType: 'Total Amount',
      amount: initialPayment + balanceAmount,
      date: finalPaymentDate,
    },
  ]);

  return newSchedule;
}

const Summary = () => {
  const {
    formData: { ticketPrice },
    updateAppData,
  } = useAppContext();
  const {
    values: { paybackPeriod },
  } = useFormikContext<IIdentityFormValues>();

  const initialDeposit = Number(ticketPrice) * 0.25;
  const balanceAfterDeposit = Number(ticketPrice) - initialDeposit;
  const interestRate = balanceAfterDeposit * (Number(paybackPeriod) / 100);
  const totalAmountPayable = balanceAfterDeposit + interestRate;
  const repaymentSchedule = useMemo(
    () =>
      generatePaymentSchedule(
        initialDeposit + BOOKING_FEE,
        totalAmountPayable,
        paybackPeriod as TenureRate
      ),
    [initialDeposit, paybackPeriod, totalAmountPayable]
  );

  const data = [
    {
      label: 'Initial deposit amount',
      value: `NGN ${formatAmountWithCommas(
        initialDeposit + BOOKING_FEE
      )} (25% of ticket price + NGN ${formatAmountWithCommas(
        BOOKING_FEE
      )} booking fee)`,
    },
    {
      label: 'Applicable interest rate',
      value: `${paybackPeriod}%`,
    },
    {
      label: 'Total amount payable before travel date',
      value: `NGN ${formatAmountWithCommas(totalAmountPayable)}`,
    },
  ];

  useEffect(() => {
    updateAppData({ initialDepositAmount: initialDeposit + BOOKING_FEE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paybackPeriod]);

  return (
    <div className="flex flex-col gap-4 mt-4">
      <table className="width: 100%; table-layout: fixed; border-collapse: collapse;">
        <thead className="rounded-t-lg">
          <tr className="rounded-t-lg">
            {tableHeader.map((header) => (
              <th
                key={header}
                className="bg-blue-gradient text-white font-medium py-3 border border-gray-200 text-lg"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {repaymentSchedule.map((item) => (
            <tr
              key={item.paymentType}
              className="text-center"
            >
              <td className="py-3 border">{item.paymentType}</td>
              <td className="py-3 border">
                NGN {formatAmountWithCommas(item.amount)}
              </td>
              <td className="py-3 border">
                {item.date.toLocaleDateString('en-NG', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="bg-blue-600/30 p-4 md:py-6 rounded-xl">
        <p className="text-center font-semibold text-lg mb-2">
          You are about to process the following request:
        </p>
        <div className="flex flex-col gap-3">
          {data.map(({ label, value }) => (
            <div
              className="grid grid-cols-2"
              key={label}
            >
              <p>{label}:</p>
              <p className="font-medium">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Summary;
